// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.TreemapChartOptionsBuilder.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import PieChartOptionsBuilder from './Izenda.PieChartOptionsBuilder';
import { exportClass } from 'share/Izenda.ServiceProvider';
import { find, maxBy, get, filter, flow, isNil, map, getOr } from 'lodash/fp';
import HighchartUtils from 'share/components/charting/highcharts/Izenda.HighchartUtils';
import { toJS } from 'mobx';

const X_AXIS_CONTAINER_NAME = 'labels';

@exportClass('TreemapChartOptionsBuilder')
export default class TreemapChartOptionsBuilder extends PieChartOptionsBuilder {
  constructor(...baseArgs) {
    super(...baseArgs);
  }

  /**
   * Build Highchart variable options for each chart type
   * @param {string} visualType Highchart chart type
   * @param {Object} userOptions User's options
   * @param {Object} dataParser Data parser
   */
  buildOptionsByType(visualType: string, userOptions: any, dataParser: any): Object {
    const basedOptions = super.buildOptionsByType(visualType, userOptions, dataParser);
    const izendaSeriesConfig = userOptions.izendaSeriesConfig || {};

    delete basedOptions.chart.type;
    delete basedOptions.xAxis.plotLines;
    delete basedOptions.yAxis.plotLines;

    let series = userOptions.series;
    let customConfig = {
      series
    };

    if (userOptions.izMultiLevel) {
      const dataStructure = dataParser.dataStructure;
      const xDatafields = dataStructure[X_AXIS_CONTAINER_NAME];
      let xAxisFields = toJS(xDatafields);

      xAxisFields.forEach(({ fieldNameAlias }) => {
        HighchartUtils.addPointActionLink(
          true,
          userOptions,
          get('plotOptions.series.dataLabels', basedOptions),
          fieldNameAlias,
          get(fieldNameAlias, izendaSeriesConfig),
          null,
          true
        );
      });

      const maxLevel = maxBy(item => item.level, series.data).level;
      const enabledDataLabels = get('plotOptions.series.dataLabels.enabled', basedOptions);
      const data = this.parseAllSeries(userOptions.series, maxLevel);
      // If all series data are removed, throw error to show message "Can't draw chart"
      if (userOptions.series.data.length > 0 && data.length === 0) {
        throw new Error('All series are negative or null!');
      }

      series = [
        {
          data,
          levels: this.buildLevelsSetting(maxLevel, enabledDataLabels),
          name: userOptions.series.serieName,
          yAxisField: series.yAxisField
        }
      ];

      customConfig = {
        chart: {
          events: {
            click: null
          }
        },
        plotOptions: {
          series: {
            point: {
              events: {
                click: null
              }
            }
          }
        },
        series
      };
    }

    return $izenda.extend(
      true,
      basedOptions,
      {
        plotOptions: {
          treemap: {
            layoutAlgorithm: 'squarified',
            showInLegend: true
          }
        }
      },
      customConfig
    );
  }

  /**
   * Merge series
   * @param {Any} series
   * @param {Number} maxLevel
   */
  parseAllSeries(series: any, maxLevel: number) {
    if (!series) {
      return [];
    }

    let tree = [];

    const { yAxisField, separatorField, separatorValue, serieName } = series;

    const concatFirstDataSets = (source: Array<any>, newCollection: Array<any>): Array<any> =>
      [].concat(newCollection, source);

    const buildTreeOfLevel = (source: any, result: Array<any>, currentLevel: Number) => {
      const { data: currentDataItems, nameField } = find(item => item.level === currentLevel, source);
      const labelName = get('fieldNameAlias', nameField);

      const filteredDataItems = flow(
        filter(x => !isNil(x.y) && x.y > 0),
        map(x => ({
          // get current object and add/override some props.
          ...x,
          id: `${x.name}_${currentLevel}_${getOr('', 'groupId.path', x)}`,
          value: x.y,
          yAxisField: yAxisField,
          nameField: nameField,
          labelName: labelName,
          separatorField: separatorField,
          separatorValue: separatorValue,
          serieName: serieName
        }))
      )(currentDataItems);

      if (currentLevel === 0) return concatFirstDataSets(result, filteredDataItems);

      const parentLevel = currentLevel - 1;
      const parentDataItems = find(item => item.level === parentLevel, source).data;

      parentDataItems.forEach(({ groupId: { path }, name }) => {
        filteredDataItems.forEach(i => {
          const currentPath = get('groupId.path', i);
          if (currentPath && currentPath.includesString(path)) {
            i.parent = `${name}_${currentLevel - 1}_${path}`;
          }
        });
      });

      result = concatFirstDataSets(result, filteredDataItems);

      return buildTreeOfLevel(source, result, parentLevel);
    };

    return buildTreeOfLevel(series.data, tree, maxLevel);
  }

  /**
   * Build levels
   * @param {Number} maxLevel
   * @param {Boolean} enabled
   */
  buildLevelsSetting(maxLevel: Number, enabled: Boolean): Array<{}> {
    return Array.from(Array(maxLevel + 1), (x, i) => ({
      level: i,
      dataLabels: {
        enabled
      }
    }));
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartChart/Izenda.TreemapChartOptionsBuilder.js